/**
 * Unified upload service with retry logic, progress tracking, and error handling
 */

export interface UploadOptions {
  endpoint?: string;
  module?: string;
  projectId?: string;
  onProgress?: (progress: number) => void;
  onStatusChange?: (status: 'pending' | 'uploading' | 'completed' | 'error') => void;
  maxRetries?: number;
  chunkSize?: number; // For chunked uploads
  compression?: {
    enabled: boolean;
    quality?: number;
    maxWidth?: number;
    maxHeight?: number;
  };
}

export interface UploadResult {
  success: boolean;
  data?: any;
  error?: string;
  referenceId?: string;
}

class UploadService {
  private static instance: UploadService;
  private activeUploads: Map<string, AbortController> = new Map();

  static getInstance(): UploadService {
    if (!UploadService.instance) {
      UploadService.instance = new UploadService();
    }
    return UploadService.instance;
  }

  /**
   * Compress image before upload
   */
  private async compressImage(
    file: File,
    options: UploadOptions['compression'] = { enabled: false }
  ): Promise<File> {
    if (!options.enabled || !file.type.startsWith('image/')) {
      return file;
    }

    return new Promise(resolve => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;
        const maxWidth = options.maxWidth || 1920;
        const maxHeight = options.maxHeight || 1080;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          blob => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          options.quality || 0.8
        );
      };

      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Upload single file with retry logic
   */
  async uploadFile(file: File, options: UploadOptions = {}): Promise<UploadResult> {
    const {
      endpoint = '/api/files/upload',
      module = 'documents',
      projectId,
      onProgress,
      onStatusChange,
      maxRetries = 3,
      compression = { enabled: false },
    } = options;

    const uploadId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const abortController = new AbortController();
    this.activeUploads.set(uploadId, abortController);

    try {
      onStatusChange?.('uploading');

      // Compress image if needed
      let fileToUpload = file;
      if (file.size > 1024 * 1024) {
        // 1MB threshold
        fileToUpload = await this.compressImage(file, compression);
      }

      const result = await this.attemptUpload(
        fileToUpload,
        { endpoint, module, projectId, onProgress },
        maxRetries,
        abortController.signal
      );

      if (result.success) {
        onStatusChange?.('completed');
      } else {
        onStatusChange?.('error');
      }

      return result;
    } catch (error) {
      onStatusChange?.('error');
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    } finally {
      this.activeUploads.delete(uploadId);
    }
  }

  /**
   * Upload multiple files in parallel with concurrency control
   */
  async uploadFiles(
    files: File[],
    options: UploadOptions = {},
    concurrentLimit: number = 3
  ): Promise<UploadResult[]> {
    const results: UploadResult[] = [];
    const uploadQueue = [...files];
    const activeUploads: Promise<void>[] = [];

    const processFile = async (): Promise<void> => {
      while (uploadQueue.length > 0) {
        const file = uploadQueue.shift();
        if (!file) continue;

        const result = await this.uploadFile(file, options);
        results.push(result);
      }
    };

    // Start concurrent uploads
    for (let i = 0; i < Math.min(concurrentLimit, files.length); i++) {
      activeUploads.push(processFile());
    }

    await Promise.all(activeUploads);
    return results;
  }

  /**
   * Attempt upload with retry logic
   */
  private async attemptUpload(
    file: File,
    options: {
      endpoint: string;
      module: string;
      projectId?: string;
      onProgress?: (progress: number) => void;
    },
    maxRetries: number,
    signal: AbortSignal,
    retryCount: number = 0
  ): Promise<UploadResult> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('module', options.module);

      if (options.projectId) {
        formData.append('projectId', options.projectId);
      }

      const response = await fetch(options.endpoint, {
        method: 'POST',
        body: formData,
        signal,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      options.onProgress?.(100);

      return {
        success: true,
        data: result.data,
        referenceId: result.data?.id || result.data?.file_name,
      };
    } catch (error) {
      if (signal.aborted) {
        return { success: false, error: 'Upload cancelled' };
      }

      // Retry logic with exponential backoff
      if (retryCount < maxRetries) {
        const delay = 1000 * Math.pow(2, retryCount);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.attemptUpload(file, options, maxRetries, signal, retryCount + 1);
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  }

  /**
   * Cancel upload by ID
   */
  cancelUpload(uploadId: string): void {
    const controller = this.activeUploads.get(uploadId);
    if (controller) {
      controller.abort();
      this.activeUploads.delete(uploadId);
    }
  }

  /**
   * Cancel all active uploads
   */
  cancelAllUploads(): void {
    this.activeUploads.forEach(controller => controller.abort());
    this.activeUploads.clear();
  }
}

export const uploadService = UploadService.getInstance();
export default uploadService;
